import React from 'react';
import { X, Info, User, Key, LogIn } from 'lucide-react';
import { useAuth } from 'react-oidc-context';
import { getEnv } from '../config/env';

interface AddAccountModalProps {
  isOpen: boolean;
  currentUsername?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export function AddAccountModal({ isOpen, currentUsername, onConfirm, onCancel }: AddAccountModalProps) {
  const auth = useAuth();
  
  if (!isOpen) return null;

  const handleOktaSignOut = () => {
    const authority = getEnv('REACT_APP_OIDC_AUTHORITY', 'https://geco-dev.oktapreview.com/oauth2/default');
    
    // Build logout URL with id_token_hint to avoid 400 error
    const params = new URLSearchParams();
    
    // Add id_token_hint if available (helps Okta validate the logout request)
    if (auth.user?.id_token) {
      params.append('id_token_hint', auth.user.id_token);
    }
    
    const logoutUrl = `${authority}/v1/logout${params.toString() ? '?' + params.toString() : ''}`;
    
    console.log('[AddAccountModal] Opening Okta logout:', logoutUrl);
    window.open(logoutUrl, '_blank');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-secondary-800 rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-secondary-700">
          <div className="flex items-center space-x-2">
            <LogIn className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Add Another Account</h2>
          </div>
          <button
            onClick={onCancel}
            className="text-gray-500 dark:text-secondary-400 hover:text-gray-700 dark:hover:text-secondary-200 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-start space-x-2">
              <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">How to add another account</p>
                <p>Due to Okta's session management, you'll need to manually sign out of Okta first, then sign in with a different account. Your current session for <strong>{currentUsername}</strong> will be saved.</p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <h3 className="font-medium text-gray-900 dark:text-white flex items-center space-x-2">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-gray-200 dark:bg-secondary-700 text-gray-700 dark:text-secondary-200 text-sm font-semibold">
                1
              </span>
              <span>Quick Way: Sign Out Button</span>
            </h3>
            
            <div className="ml-8 space-y-2 text-sm text-gray-600 dark:text-secondary-400">
              <p>
                Click the <strong>"Sign Out of Okta"</strong> button below to open Okta logout in a new tab. After signing out, close that tab and click "Continue to Login" to add your new account.
              </p>
            </div>
          </div>

          <div className="space-y-3">
            <h3 className="font-medium text-gray-900 dark:text-white flex items-center space-x-2">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-gray-200 dark:bg-secondary-700 text-gray-700 dark:text-secondary-200 text-sm font-semibold">
                2
              </span>
              <span>Or: Manual Sign Out</span>
            </h3>
            
            <div className="ml-8 space-y-2 text-sm text-gray-600 dark:text-secondary-400">
              <p>
                If you prefer, click "Continue to Login" first. If Okta shows only a password field:
              </p>
              <div className="bg-gray-50 dark:bg-secondary-900 border border-gray-200 dark:border-secondary-700 rounded p-3 font-mono text-xs">
                <ol className="list-decimal ml-4 space-y-1">
                  <li>Look for "Sign out" or your username at the top-right</li>
                  <li>Click and select "Sign out"</li>
                  <li>After signing out, you'll see the full login form</li>
                  <li>Enter your different email address and password</li>
                </ol>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <h3 className="font-medium text-gray-900 dark:text-white flex items-center space-x-2">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-gray-200 dark:bg-secondary-700 text-gray-700 dark:text-secondary-200 text-sm font-semibold">
                3
              </span>
              <span>Alternative: Use Browser Features</span>
            </h3>
            
            <div className="ml-8 space-y-3 text-sm text-gray-600 dark:text-secondary-400">
              <div className="flex items-start space-x-2">
                <User className="h-4 w-4 text-gray-400 dark:text-secondary-500 flex-shrink-0 mt-0.5" />
                <p>
                  <strong>Incognito/Private Window:</strong> Open a new private window and sign in with the second account
                </p>
              </div>
              
              <div className="flex items-start space-x-2">
                <Key className="h-4 w-4 text-gray-400 dark:text-secondary-500 flex-shrink-0 mt-0.5" />
                <p>
                  <strong>Browser Profiles:</strong> Create separate browser profiles for each account (best for regular use)
                </p>
              </div>
            </div>
          </div>

          <div className="bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
            <p className="text-xs text-amber-800">
              <strong>💡 Tip:</strong> For easier multi-account management, consider using different browser profiles or containers for each account.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center space-x-3 p-6 border-t border-gray-200 dark:border-secondary-700 bg-gray-50 dark:bg-secondary-900 rounded-b-lg">
          <button
            onClick={handleOktaSignOut}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-secondary-200 bg-white dark:bg-secondary-800 border border-gray-300 dark:border-secondary-600 rounded-md hover:bg-gray-50 dark:hover:bg-secondary-700 transition-colors flex items-center space-x-2"
            title="Opens Okta logout in new tab to clear your session"
          >
            <LogIn className="h-4 w-4 transform rotate-180" />
            <span>Sign Out of Okta</span>
          </button>
          
          <div className="flex space-x-3">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-secondary-200 bg-white dark:bg-secondary-800 border border-gray-300 dark:border-secondary-600 rounded-md hover:bg-gray-50 dark:hover:bg-secondary-700 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors flex items-center space-x-2"
            >
              <LogIn className="h-4 w-4" />
              <span>Continue to Login</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
